import {
  App,
  Button,
  Card,
  Empty,
  Form,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  theme as antdTheme,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs from 'dayjs';
import { academicsApi, type TimetableEntry } from '@/api/academics';
import { setupApi } from '@/api/setup';
import { hrApi } from '@/api/hr';
import { useAuthStore } from '@/store/auth';
import { useAbility } from '@/auth/ability-provider';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface SlotForm {
  subjectId: number;
  teacherId: number;
}

interface CellTarget {
  periodSlotId: number;
  dayOfWeek: number;
  existing: TimetableEntry | null;
}

export function TimetablePage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  // ACADEMICS:MANAGE_TIMETABLE (V13) is the real edit gate now — plain Teacher
  // still holds ACADEMICS:EDIT for marks but must not rewrite the schedule.
  const ability = useAbility();
  const canEdit = ability.can('manage', 'Timetable');
  const academicYearId = useAuthStore((s) => s.academicYearId);
  const scope = useAuthStore((s) => s.user?.scope);
  // Teachers (and any other unbounded=false caller) only see sections they're
  // assigned to via the timetable. Super Admin / Principal etc. get the full
  // list.
  const ownedSectionIds = scope && !scope.unbounded
    ? new Set(scope.teacherClassSectionIds)
    : null;
  const { token } = antdTheme.useToken();

  const [classSectionId, setClassSectionId] = useState<number | undefined>(undefined);
  const [target, setTarget] = useState<CellTarget | null>(null);
  const [form] = Form.useForm<SlotForm>();

  const { data: allSections } = useQuery({
    queryKey: ['setup', 'class-sections', academicYearId],
    queryFn: () => setupApi.listClassSections(academicYearId ?? undefined),
    staleTime: 60_000,
  });
  const sections = ownedSectionIds
    ? (allSections ?? []).filter((s) => ownedSectionIds.has(s.id))
    : allSections;

  const { data: periods } = useQuery({
    queryKey: ['academics', 'period-slots', 'active'],
    queryFn: () => academicsApi.listPeriodSlots(true),
    staleTime: 5 * 60_000,
  });

  const { data: subjects } = useQuery({
    queryKey: ['setup', 'lookups', 'subjects', 'active'],
    queryFn: () => setupApi.listLookup('subjects', true),
    staleTime: 5 * 60_000,
  });

  const { data: staffPage } = useQuery({
    queryKey: ['hr', 'staff', 'active', 'all'],
    queryFn: () => hrApi.searchStaff({ active: true, size: 500 }),
    staleTime: 60_000,
  });
  const teachers = staffPage?.rows ?? [];

  const { data: entries, isFetching } = useQuery({
    queryKey: ['academics', 'timetable', classSectionId],
    queryFn: () => academicsApi.sectionTimetable(classSectionId!),
    enabled: classSectionId != null,
  });

  const upsert = useMutation({
    mutationFn: (v: SlotForm) =>
      academicsApi.upsertTimetable({
        classSectionId: classSectionId!,
        periodSlotId: target!.periodSlotId,
        dayOfWeek: target!.dayOfWeek,
        subjectId: v.subjectId,
        teacherId: v.teacherId,
      }),
    onSuccess: () => {
      message.success('Timetable updated');
      qc.invalidateQueries({ queryKey: ['academics', 'timetable', classSectionId] });
      setTarget(null);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => academicsApi.deleteTimetable(id),
    onSuccess: () => {
      message.success('Cell cleared');
      qc.invalidateQueries({ queryKey: ['academics', 'timetable', classSectionId] });
      setTarget(null);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const open = (cell: CellTarget) => {
    if (!canEdit) return;
    setTarget(cell);
    form.resetFields();
    if (cell.existing) {
      form.setFieldsValue({ subjectId: cell.existing.subjectId, teacherId: cell.existing.teacherId });
    }
  };

  const grid: Record<string, TimetableEntry> = {};
  for (const e of entries ?? []) {
    grid[`${e.periodSlotId}-${e.dayOfWeek}`] = e;
  }

  const columns = [
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
      fixed: 'left' as const,
      width: 160,
      render: (_: unknown, row: { period: typeof periods extends (infer T)[] | undefined ? T : never }) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.period.name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {dayjs(row.period.startTime, 'HH:mm:ss').format('HH:mm')} – {dayjs(row.period.endTime, 'HH:mm:ss').format('HH:mm')}
          </Typography.Text>
        </Space>
      ),
    },
    ...DAY_NAMES.map((d, idx) => ({
      title: d,
      key: `day-${idx}`,
      width: 180,
      render: (_: unknown, row: { period: { id: number } }) => {
        const entry = grid[`${row.period.id}-${idx}`];
        return (
          <div
            onClick={() => open({ periodSlotId: row.period.id, dayOfWeek: idx, existing: entry ?? null })}
            style={{
              cursor: canEdit ? 'pointer' : 'default',
              borderRadius: 6,
              padding: 6,
              minHeight: 56,
              background: entry ? token.colorFillTertiary : 'transparent',
              border: entry ? `1px solid ${token.colorBorderSecondary}` : `1px dashed ${token.colorBorderSecondary}`,
            }}
          >
            {entry ? (
              <Space orientation="vertical" size={0} style={{ width: '100%' }}>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {entry.subjectName}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {entry.teacherName}
                </Typography.Text>
              </Space>
            ) : canEdit ? (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                + add
              </Typography.Text>
            ) : null}
          </div>
        );
      },
    })),
  ];

  const dataSource = (periods ?? []).map((p) => ({ key: p.id, period: p }));

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Academics"
        title="Timetable"
        description="Click any cell to assign or change subject and teacher. Teacher conflicts across sections are blocked."
      />

      <Space wrap size={12}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder={ownedSectionIds && ownedSectionIds.size === 0
            ? 'You have no assigned sections this year'
            : 'Pick a class section'}
          style={{ minWidth: 360 }}
          value={classSectionId}
          onChange={(v) => setClassSectionId(v)}
          disabled={ownedSectionIds != null && ownedSectionIds.size === 0}
          options={(sections ?? []).map((s) => ({
            value: s.id,
            label: `${s.className} · ${s.sectionName} · ${s.academicYearName}`,
          }))}
        />
        {ownedSectionIds && (
          <Tag color="processing">
            Showing your {ownedSectionIds.size} assigned section{ownedSectionIds.size === 1 ? '' : 's'}
          </Tag>
        )}
      </Space>

      {classSectionId == null ? (
        <Empty description={
          ownedSectionIds && ownedSectionIds.size === 0
            ? "You aren't on the timetable yet. Ask the Principal or office to assign you."
            : 'Pick a class section to view its timetable'
        } />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            size="small"
            loading={isFetching}
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      )}

      <Modal
        title={
          target
            ? `${DAY_NAMES[target.dayOfWeek]} · ${
                periods?.find((p) => p.id === target.periodSlotId)?.name ?? 'Period'
              }`
            : ''
        }
        open={target != null}
        onCancel={() => setTarget(null)}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText="Save"
        okButtonProps={{ icon: <PlusOutlined /> }}
        destroyOnHidden
        footer={(_, { OkBtn, CancelBtn }) => (
          <Space style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            {target?.existing ? (
              <Popconfirm
                title="Clear this cell?"
                onConfirm={() => target.existing && remove.mutate(target.existing.id)}
                okText="Clear"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />} loading={remove.isPending}>
                  Clear
                </Button>
              </Popconfirm>
            ) : (
              <span />
            )}
            <Space>
              <CancelBtn />
              <OkBtn />
            </Space>
          </Space>
        )}
      >
        {target && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Subject" name="subjectId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(subjects ?? []).map((s) => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
            <Form.Item label="Teacher" name="teacherId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={teachers.map((s) => ({
                  value: s.id,
                  label: `${s.firstName} ${s.lastName}${s.designationName ? ` · ${s.designationName}` : ''}`,
                }))}
              />
            </Form.Item>
            {target.existing && (
              <Tag color="processing">Currently: {target.existing.subjectName} · {target.existing.teacherName}</Tag>
            )}
          </Form>
        )}
      </Modal>
    </div>
  );
}
